import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { ImportarFavorecidos } from './ImportarFavorecidos'
import { norm } from './finance-shared'
import { PanelShell, PlnBarra, PlnBarraSep, PlnBloco, stickyTh } from './PanelShell'
import { NeuSwitch } from './NeuSwitch'
import {
  FiPlus, FiSearch, FiEdit2, FiChevronUp, FiChevronDown, FiLoader,
  FiChevronLeft, FiChevronRight, FiUsers, FiTrash2, FiUploadCloud,
} from 'react-icons/fi'

interface Favorecido {
  id: number; name: string; person_type: 'F' | 'J'; document: string | null; category: string | null
  pix_key: string | null; bank_code: string | null; bank_name: string | null; agency: string | null
  operation: string | null; account_number: string | null; email: string | null; phone: string | null
  is_active: 0 | 1; notes: string | null
}
type Status = 'active' | 'inactive' | 'all'
type Sort = 'name' | 'category'
interface Counts { active: number; inactive: number; all: number }

const PAGE_SIZE = 10

// Janela de páginas (1 … p-1 p p+1 … N) — evita renderizar um botão por página
// quando há muitas (ex.: 299 favorecidos / 10 por página = 30 páginas).
function pageWindow(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const mid = [current - 1, current, current + 1].filter(n => n > 1 && n < total)
  const out: (number | '…')[] = [1]
  if (mid[0] > 2) out.push('…')
  out.push(...mid)
  if (mid[mid.length - 1] < total - 1) out.push('…')
  out.push(total)
  return out
}

export function Favorecidos() {
  const { authenticatedFetch } = useAuth()
  const [items, setItems] = useState<Favorecido[]>([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<Counts>({ active: 0, inactive: 0, all: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<Status>('active')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Sort>('name')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [modal, setModal] = useState<{ fav?: Favorecido } | null>(null)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const qs = new URLSearchParams({ status, search, sort, dir, page: String(page), pageSize: String(PAGE_SIZE) })
      const r = await authenticatedFetch(`/api/favorecidos?${qs}`)
      const d = await r.json()
      if (d.success) { setItems(d.items); setTotal(d.total); setCounts(d.counts) }
      else setError(d.error || 'Falha ao carregar')
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, status, search, sort, dir, page])

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])
  useEffect(() => { setPage(1) }, [status, search])

  const toggleSort = (col: Sort) => {
    if (sort === col) setDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(col); setDir('asc') }
  }

  const setActive = async (f: Favorecido, active: boolean) => {
    const r = await authenticatedFetch(`/api/favorecidos/${f.id}`, { method: 'PUT', body: JSON.stringify({ is_active: active }) })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha'); return }
    load()
  }

  const remove = async (f: Favorecido) => {
    if (!window.confirm(`Inativar "${f.name}"? Ele deixa de aparecer nas listas, mas o histórico é preservado.`)) return
    const r = await authenticatedFetch(`/api/favorecidos/${f.id}`, { method: 'DELETE' })
    const d = await r.json()
    if (!d.success) { alert(d.error || 'Falha ao inativar'); return }
    load()
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const tabs: Array<{ key: Status; label: string; n: number }> = [
    { key: 'active', label: 'Ativos', n: counts.active },
    { key: 'inactive', label: 'Inativos', n: counts.inactive },
    { key: 'all', label: 'Todos', n: counts.all },
  ]

  const SortHead = ({ col, children }: { col: Sort; children: React.ReactNode }) => (
    <button onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 hover:text-foreground">
      {children}
      {sort === col
        ? (dir === 'asc' ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />)
        : <FiChevronUp className="w-3.5 h-3.5 opacity-20" />}
    </button>
  )

  return (
    <PanelShell
      titulo="Favorecidos"
      sub="Quem recebe os pagamentos: dados, PIX e conta bancária"
      icone={<FiUsers />}
      acoes={<>
        <button
          onClick={() => setImporting(true)}
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border text-sm font-medium px-3 py-2 hover:bg-secondary"
        >
          <FiUploadCloud className="w-4 h-4" /> Importar
        </button>
        <button
          onClick={() => setModal({})}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:opacity-90"
        >
          <FiPlus className="w-4 h-4" /> Novo favorecido
        </button>
      </>}>
      {/* Toolbar — PLANOS (21/10/2026): busca + abas de situação numa PlnBarra;
          Importar/Novo subiram para as ações do cabeçalho. */}
      <PlnBarra>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, CPF/CNPJ ou categoria…"
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
        <PlnBarraSep />
        {/* Tabs contadores */}
        <div className="neu-seg inline-flex items-center gap-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              status === t.key ? 'neu-relief bg-primary/10 text-primary' : 'neu-seg-btn text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({t.n})</span>
          </button>
        ))}
        </div>
      </PlnBarra>

      {error && <div className="shrink-0 text-sm text-red-500">{error}</div>}

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell).
          PLANOS (21/10/2026): dentro de PlnBloco; contagem na nota e paginação
          no `extra` do cabeçalho do bloco (antes ficavam soltas abaixo). */}
      <PlnBloco
        titulo="Favorecidos"
        nota={loading ? undefined : `${total} registro(s)${totalPages > 1 ? ` · página ${page} de ${totalPages}` : ''}`}
        extra={totalPages > 1 ? (
          <div className="flex items-center gap-1 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-border disabled:opacity-40 hover:bg-secondary">
              <FiChevronLeft className="w-4 h-4" />
            </button>
            {pageWindow(page, totalPages).map((n, i) => n === '…' ? (
              <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-muted-foreground">…</span>
            ) : (
              <button key={n} onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-md text-sm ${n === page ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-secondary'}`}>
                {n}
              </button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-border disabled:opacity-40 hover:bg-secondary">
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : undefined}
        className="flex-1 min-h-[280px]"
      >
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}><SortHead col="name">Nome</SortHead></th>
              <th className={`px-4 py-2.5 font-medium hidden sm:table-cell ${stickyTh}`}>CPF/CNPJ</th>
              <th className={`px-4 py-2.5 font-medium hidden md:table-cell ${stickyTh}`}><SortHead col="category">Categoria</SortHead></th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Situação</th>
              <th className={`px-4 py-2.5 font-medium w-24 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Nenhum favorecido{search ? ' encontrado' : ' cadastrado'}.</td></tr>
            ) : items.map(f => (
              <tr key={f.id} className="neu-tr border-b border-border/60 last:border-0 hover:bg-secondary/40">
                <td className="px-4 py-2.5 text-foreground">{f.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">{f.document || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{f.category || '—'}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${
                    f.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {f.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Editar" onClick={() => setModal({ fav: f })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <NeuSwitch on={f.is_active} onClick={() => setActive(f, !f.is_active)}
                      title={f.is_active ? 'Inativar' : 'Reativar'} />
                    <button title="Inativar" onClick={() => remove(f)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-secondary">
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </PlnBloco>

      {modal && (
        <FavorecidoModal
          fav={modal.fav}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}

      {importing && (
        <ImportarFavorecidos onClose={() => setImporting(false)} onImported={() => { setImporting(false); load() }} />
      )}
    </PanelShell>
  )
}

function FavorecidoModal({ fav, onClose, onSaved }: { fav?: Favorecido; onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [form, setForm] = useState({
    name: fav?.name || '', person_type: fav?.person_type || 'J', document: fav?.document || '',
    category: fav?.category || '', pix_key: fav?.pix_key || '', bank_code: fav?.bank_code || '',
    bank_name: fav?.bank_name || '', agency: fav?.agency || '', account_number: fav?.account_number || '',
    email: fav?.email || '', phone: fav?.phone || '', notes: fav?.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Categorias FINANCEIRAS de despesa pro autocomplete do campo Categoria.
  // Escolher uma daqui liga o elo com o lançamento: o TxnModal casa por nome
  // (sem acento/caixa) e preenche a categoria sozinho ao selecionar o
  // favorecido. Texto livre continua valendo — o campo é string no banco, sem
  // FK (legado dos importados com "Fornecedor" genérico).
  const [finCats, setFinCats] = useState<string[]>([])
  const [catOpen, setCatOpen] = useState(false)
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  useEffect(() => {
    (async () => {
      const r = await authenticatedFetch('/api/categories?kind=despesa')
      const d = await r.json()
      if (d.success) setFinCats(d.categories.map((c: { name: string }) => c.name))
    })().catch(() => { /* autocomplete é conveniência — o campo segue como texto livre */ })
  }, [authenticatedFetch])

  // Filtro client-side sem acento/caixa; campo vazio mostra a lista completa
  const catFiltered = form.category.trim()
    ? finCats.filter(n => norm(n).includes(norm(form.category)))
    : finCats

  const save = async () => {
    if (!form.name.trim()) { setErr('Informe o nome.'); return }
    setSaving(true); setErr(null)
    try {
      const r = fav
        ? await authenticatedFetch(`/api/favorecidos/${fav.id}`, { method: 'PUT', body: JSON.stringify(form) })
        : await authenticatedFetch('/api/favorecidos', { method: 'POST', body: JSON.stringify(form) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao salvar'); return }
      onSaved()
    } catch {
      setErr('Erro de conexão')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'neu-convex w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
  const labelCls = 'block text-sm font-medium text-foreground mb-1'

  return (
    <SideModal
      title={fav ? 'Editar favorecido' : 'Novo favorecido'}
      subtitle="Quem recebe o pagamento"
      icon={FiUsers}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="neu-tab px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Salvar favorecido
          </button>
        </>
      }
    >
      {err && <div className="text-sm text-red-500">{err}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Nome*</label>
          <input autoFocus value={form.name} onChange={set('name')} className={inputCls} placeholder="Ex.: Fornecedor X Ltda" />
        </div>
        <div>
          <label className={labelCls}>Tipo</label>
          <select value={form.person_type} onChange={set('person_type')} className={inputCls}>
            <option value="J">Jurídica</option>
            <option value="F">Física</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>CPF/CNPJ</label>
          <input value={form.document} onChange={set('document')} className={inputCls} />
        </div>
        <div className="col-span-2 relative">
          <label className={labelCls}>Categoria</label>
          <input value={form.category} autoComplete="off"
            title="Escolha uma categoria financeira (liga o preenchimento automático da categoria no lançamento) ou digite texto livre"
            onChange={e => { set('category')(e); setCatOpen(true) }}
            onFocus={() => setCatOpen(true)}
            onKeyDown={e => { if (e.key === 'Escape' && catOpen) { e.stopPropagation(); setCatOpen(false) } }}
            onBlur={() => setTimeout(() => setCatOpen(false), 150)}
            className={inputCls} placeholder="Ex.: Fornecedor" />
          {catOpen && catFiltered.length > 0 && (
            <ul className="neu-pop absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg glass-popover">
              {catFiltered.map(n => (
                <li key={n}>
                  {/* onMouseDown previne o blur do input antes do onClick disparar */}
                  <button type="button" onMouseDown={e => e.preventDefault()}
                    onClick={() => { setForm(f => ({ ...f, category: n })); setCatOpen(false) }}
                    title="Usar esta categoria financeira"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary text-foreground">
                    {n}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Chave PIX</label>
          <input value={form.pix_key} onChange={set('pix_key')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Banco</label>
          <input value={form.bank_name} onChange={set('bank_name')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Código do banco</label>
          <input value={form.bank_code} onChange={set('bank_code')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Agência</label>
          <input value={form.agency} onChange={set('agency')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Conta</label>
          <input value={form.account_number} onChange={set('account_number')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>E-mail</label>
          <input value={form.email} onChange={set('email')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Telefone</label>
          <input value={form.phone} onChange={set('phone')} className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Observações</label>
          <textarea value={form.notes} onChange={set('notes')} className={inputCls} rows={2} />
        </div>
      </div>
    </SideModal>
  )
}
